import { Check, Close, Delete, Edit } from "@mui/icons-material";
import { Tooltip, IconButton, Button, CircularProgress } from "@mui/material";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { useState } from "react";
import { useRevalidator } from "react-router-dom";

import useService from "../../../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../../../global/ApiRequestErrorHandler";
import ModalShell from "../../../../../../global/ModalShell";

import InlineResponse from "./InlineReponse";

export default function AvailabilityHourWindow({
  index,
  activeWindow,
  client,
  dayKey,
  formValues,
  setFormValues,
  newField,
}) {
  const { revalidate } = useRevalidator();
  const caseAvailabilityRepository = useService("repositories/case-availability-repository");
  const [responseBreakdown, setResponseBreakdown] = useState("");
  const [responseSuccess, setResponseSuccess] = useState(null);
  const [startTime, setStartTime] = useState(
    activeWindow ? new Date(`2024-06-25T${activeWindow?.timeStart}`) : ""
  );
  const [endTime, setEndTime] = useState(
    activeWindow ? new Date(`2024-06-25T${activeWindow?.timeEnd}`) : ""
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toggleEdit, setToggleEdit] = useState(false);
  const formatTime = "HH:mm";

  const handleApiError = (error) => {
    setLoading(false);
    const res = !error.response ? error.response : error.response.data;
    const errArr = ApiRequestErrorHandler(res);
    setResponseBreakdown(errArr[0]);
    setResponseSuccess(false);
    setTimeout(() => {
      setResponseSuccess(null);
      setResponseBreakdown("");
    }, 5000);
  };

  const handleApiSuccess = () => {
    setLoading(false);
    revalidate();
    setResponseSuccess(true);
    setToggleEdit(false);
    setTimeout(() => {
      setResponseSuccess(null);
      setResponseBreakdown("");
    }, 5000);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      let time_start = startTime;
      let time_end = endTime;
      if (startTime instanceof Date) {
        time_start = format(startTime, formatTime);
      }
      if (endTime instanceof Date) {
        time_end = format(endTime, formatTime);
      }
      const body = {
        day: dayKey,
        time_end,
        time_start,
        user: client,
      };

      if (activeWindow?.id) {
        await caseAvailabilityRepository.patch(activeWindow.id, body);
        setResponseBreakdown("Client Availability Updated");
      } else {
        await caseAvailabilityRepository.createRecord(body);
        setResponseBreakdown("Client Availability Added");
        removeFormFields(index);
      }
      handleApiSuccess();
    } catch (error) {
      handleApiError(error);
    }
  };

  const deleteExistingTimeWindow = async () => {
    try {
      await caseAvailabilityRepository.delete(activeWindow.id);
      setResponseBreakdown("Client Availability Deleted");
      setToggleEdit(false);
      revalidate();
    } catch (error) {
      handleApiError(error);
    }
  };

  const removeFormFields = (i) => {
    const newFormValues = [...formValues];
    newFormValues.splice(i, 1);
    setFormValues(newFormValues);
  };

  return (
    <>
      <div className={`${!toggleEdit && !newField ? "m-1 border-y-2" : ""}`}>
        {!toggleEdit && !newField ? (
          <div className="flex flex-col items-start justify-between">
            <div className="flex w-full flex-row items-center justify-between">
              <p className="p-2 text-center text-xs">
                {format(new Date(`2024-06-25T${activeWindow.timeStart}`), "hh:mm aa")}
                {" - "}
                {format(new Date(`2024-06-25T${activeWindow.timeEnd}`), "hh:mm aa")}
              </p>
              <IconButton onClick={() => setToggleEdit(true)}>
                <Edit className="text-[18px] text-gray-400" />
              </IconButton>
            </div>
          </div>
        ) : responseBreakdown && responseSuccess ? (
          ""
        ) : (
          <div className="flex w-full flex-row items-center">
            <div className="mr-4 flex w-3/4 flex-col md:flex-row">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <TimePicker
                  className="mx-1 my-2 w-1/2"
                  label="Start"
                  value={startTime}
                  views={["hours", "minutes"]}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                  onChange={(newValue) => setStartTime(newValue)}
                />
                <TimePicker
                  className="mx-1 my-2 w-1/2"
                  label="End"
                  value={endTime}
                  slotProps={{
                    textField: {
                      size: "small",
                    },
                  }}
                  views={["hours", "minutes"]}
                  onChange={(newValue) => setEndTime(newValue)}
                />
              </LocalizationProvider>
            </div>

            <div className="p-0.1 w-1/4">
              <Tooltip title="Submit">
                <IconButton
                  className="mr-2 text-green-600"
                  onClick={handleSubmit}
                  size="small"
                  disabled={loading || !(startTime instanceof Date) || !(endTime instanceof Date)}
                >
                  <Check className="text-[20px]" sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>

              {!activeWindow ? (
                ""
              ) : (
                <Tooltip title="Delete existing time window">
                  <IconButton onClick={() => setConfirmDelete(true)} className="mr-2 text-red-600">
                    <Delete sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Cancel">
                <IconButton
                  className="mr-2 text-red-600"
                  onClick={() => (!activeWindow ? removeFormFields(index) : setToggleEdit(false))}
                  size="small"
                >
                  <Close className="text-[20px]" sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        )}
        {loading ? (
          <CircularProgress color="secondary" />
        ) : (
          <InlineResponse responseBreakdown={responseBreakdown} responseSuccess={responseSuccess} />
        )}
      </div>

      {!activeWindow ? (
        ""
      ) : (
        <ModalShell
          open={confirmDelete}
          setOpen={setConfirmDelete}
          color={"red"}
          title="Confirm time window removal"
        >
          <div>
            <p className="text-center">Are you sure you want to remove this time window?</p>
            <p className="text-center font-bold">
              {new Date(`2024-06-25T${activeWindow.timeStart}`).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              -{" "}
              {new Date(`2024-06-25T${activeWindow.timeEnd}`).toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
            </p>
            <div className="mx-auto my-2 w-fit">
              {" "}
              <Button
                onClick={() => deleteExistingTimeWindow()}
                endIcon={<Delete />}
                variant="outlined"
                className="border-red-600 text-red-600"
                disabled={loading}
              >
                Confirm Delete
              </Button>
            </div>
          </div>
        </ModalShell>
      )}
    </>
  );
}
